import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, auth, firebaseStatus } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState(firebaseStatus); // 'configured', 'demo', 'error'

  const signInWithGoogle = async () => {
    try {
      console.log('🔍 Google Sign-In Debug:');
      console.log('Supabase client exists:', !!supabase);
      console.log('Auth mode:', authMode);
      
      if (!supabase) {
        console.error('❌ Supabase client not initialized');
        throw new Error('Authentication service not configured. Please contact support.');
      }

      // Test Supabase connection first
      const { data: testData, error: testError } = await supabase.auth.getSession();
      console.log('🔍 Supabase connection test:', { testData: !!testData, testError });
      
      if (testError) {
        console.error('❌ Supabase connection failed:', testError);
        throw new Error('Authentication service connection failed. Please try again.');
      }

      // Real Supabase authentication only
      console.log('🔄 Attempting Google Sign-In via Supabase...');
      console.log('✅ Using Supabase Google OAuth');
      console.log('🔍 Current origin:', window.location.origin);
      
      // Use the correct production URL for redirects
      const redirectUrl = window.location.origin.includes('localhost') 
        ? `${window.location.origin}/dashboard`
        : `${window.location.origin}/dashboard`;
      
      console.log('🔗 Redirect URL:', redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        console.error('❌ Supabase OAuth error:', error);
        throw error;
      }

      console.log('✅ Google Sign-In initiated successfully');
      console.log('🔄 Redirecting to Google for authentication...');
      
      // For OAuth flow, the user will be set via the auth state change listener
      // The redirect will happen automatically
      return data;
    } catch (error) {
      console.error('❌ Google Sign-In failed:', error);
      
      // Provide specific error messages
      if (error.message.includes('popup')) {
        throw new Error('Sign-in was cancelled. Please try again.');
      } else if (error.message.includes('network')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      
      throw new Error(`Authentication failed: ${error.message}. Please contact support if this continues.`);
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      if (!supabase) {
        throw new Error('Authentication service not configured. Please contact support.');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      // Post-authentication redirect will be handled by auth state change
      return data.user;
    } catch (error) {
      console.error('Error signing in with email:', error);
      
      // Transform Supabase errors to Firebase-like errors for compatibility
      if (error.message.includes('Invalid login credentials')) {
        error.code = 'auth/wrong-password';
      } else if (error.message.includes('Email not confirmed')) {
        error.code = 'auth/email-not-verified';
      } else if (error.message.includes('User not found')) {
        error.code = 'auth/user-not-found';
      }
      
      throw error;
    }
  };

  const signUpWithEmail = async (email, password, displayName) => {
    try {
      console.log('🔍 Email Sign-Up Debug:');
      console.log('Supabase client exists:', !!supabase);
      console.log('Auth mode:', authMode);
      
      if (!supabase) {
        console.error('❌ Supabase client not initialized for email signup');
        throw new Error('Authentication service not configured. Please contact support.');
      }

      // Test Supabase connection first
      const { data: testData, error: testError } = await supabase.auth.getSession();
      console.log('🔍 Supabase connection test for signup:', { testData: !!testData, testError });
      
      if (testError) {
        console.error('❌ Supabase connection failed during signup:', testError);
        throw new Error('Authentication service connection failed. Please try again.');
      }

      console.log('🔄 Attempting email sign-up via Supabase...');

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: displayName,
            name: displayName
          }
        }
      });

      if (error) {
        console.error('❌ Supabase sign-up error:', error);
        throw error;
      }

      console.log('✅ Email sign-up successful:', !!data.user);

      // Create user profile in Supabase
      if (data.user) {
        const userWithName = {
          uid: data.user.id,
          email: data.user.email,
          displayName: displayName,
          photoURL: null
        };
        await createUserProfile(userWithName);
        
        // Post-authentication redirect will be handled by auth state change
        return userWithName;
      }

      return data.user;
    } catch (error) {
      console.error('Error signing up with email:', error);
      
      // Transform Supabase errors to Firebase-like errors for compatibility
      if (error.message.includes('already registered')) {
        error.code = 'auth/email-already-in-use';
      } else if (error.message.includes('Password should be')) {
        error.code = 'auth/weak-password';
      } else if (error.message.includes('Invalid email')) {
        error.code = 'auth/invalid-email';
      }
      
      throw error;
    }
  };

  const createUserProfile = async (user) => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured - cannot create user profile');
        return null;
      }

      // Check if profile exists in Supabase
      const { data: existingProfile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('uid', user.uid)
        .single();

      if (existingProfile) {
        // Sync existing user to MongoDB
        await syncUserToMongoDB(user);
        return existingProfile;
      }

      // Create new profile in Supabase
      const userProfile = {
        uid: user.uid,
        display_name: user.displayName,
        email: user.email,
        photo_url: user.photoURL,
        created_at: new Date().toISOString(),
        total_resumes: 0,
        total_downloads: 0,
        average_score: 0,
        rank: Math.floor(Math.random() * 100) + 1,
        badges: []
      };

      const { data, error } = await supabase
        .from('user_profiles')
        .insert([userProfile])
        .select()
        .single();

      if (error) {
        console.error('Error creating user profile:', error);
        // Still attempt MongoDB sync for auth consistency
        await syncUserToMongoDB(user);
        
        // Return a default profile structure
        return {
          uid: user.uid,
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
          createdAt: new Date().toISOString(),
          stats: {
            totalResumes: 0,
            totalDownloads: 0,
            averageScore: 0,
            rank: Math.floor(Math.random() * 100) + 1,
            badges: []
          }
        };
      }

      // Sync new user to MongoDB
      await syncUserToMongoDB(user);

      // Transform back to expected format
      return {
        uid: data.uid,
        displayName: data.display_name,
        email: data.email,
        photoURL: data.photo_url,
        createdAt: data.created_at,
        stats: {
          totalResumes: data.total_resumes,
          totalDownloads: data.total_downloads,
          averageScore: data.average_score,
          rank: data.rank,
          badges: data.badges || []
        }
      };
    } catch (error) {
      console.error('Error creating user profile:', error);
      return null;
    }
  };

  // NEW: Sync user data to MongoDB for hybrid architecture
  const syncUserToMongoDB = async (user) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        console.warn('Backend URL not configured - skipping MongoDB sync');
        return;
      }

      if (!user || !user.uid) {
        console.warn('User or user.uid is null - skipping MongoDB sync');
        return;
      }

      console.log('🔄 Syncing user to MongoDB:', user.uid);

      const response = await fetch(`${backendUrl}/api/user/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        })
      });

      if (response.ok) {
        const syncResult = await response.json();
        console.log('✅ User synced to MongoDB successfully:', syncResult);
      } else {
        console.warn('⚠️ MongoDB sync failed:', response.status, response.statusText);
        // Don't throw error - sync is optional
      }
    } catch (error) {
      console.error('❌ Error syncing user to MongoDB:', error.message);
      // Check if it's a CORS error and provide helpful message
      if (error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
        console.warn('🔧 MongoDB sync blocked by CORS - this is non-critical and won\'t affect app functionality');
      }
      // Don't throw error - sync is optional for user experience
    }
  };

  const getUserProfile = async (uid) => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured - cannot get user profile');
        return null;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('uid', uid)
        .single();

      if (error) {
        console.error('Error getting user profile:', error);
        return null;
      }

      // Transform to expected format
      return {
        uid: data.uid,
        displayName: data.display_name,
        email: data.email,
        photoURL: data.photo_url,
        createdAt: data.created_at,
        stats: {
          totalResumes: data.total_resumes,
          totalDownloads: data.total_downloads,
          averageScore: data.average_score,
          rank: data.rank,
          badges: data.badges || []
        }
      };
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  const updateUserStats = async (uid, stats) => {
    try {
      if (!supabase) {
        console.warn('Supabase not configured - cannot update user stats');
        return;
      }

      // Transform stats to database format
      const updateData = {};
      if (stats.totalResumes !== undefined) updateData.total_resumes = stats.totalResumes;
      if (stats.totalDownloads !== undefined) updateData.total_downloads = stats.totalDownloads;
      if (stats.averageScore !== undefined) updateData.average_score = stats.averageScore;
      if (stats.rank !== undefined) updateData.rank = stats.rank;
      if (stats.badges !== undefined) updateData.badges = stats.badges;

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('uid', uid);

      if (error) {
        console.error('Error updating user stats:', error);
      }
    } catch (error) {
      console.error('Error updating user stats:', error);
    }
  };

  const logout = async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      setUser(null);
      
      // Redirect to home after logout
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      setUser(null);
      window.location.href = '/';
    }
  };

  useEffect(() => {
    // Set up Supabase auth listener
    if (auth.onAuthStateChanged) {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        console.log('🔄 Auth state changed:', user ? 'User logged in' : 'User logged out');
        
        if (user) {
          console.log('👤 User details:', {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName
          });
          
          // Get or create user profile
          await createUserProfile(user);
          
          // Check if we're on the home page and should redirect to dashboard
          if (window.location.pathname === '/') {
            console.log('🚀 Redirecting to dashboard after successful auth');
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 100);
          }
        }
        setUser(user);
        setLoading(false);
      });
      return unsubscribe;
    } else {
      setLoading(false);
    }
  }, []);

  const value = {
    user,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    getUserProfile,
    updateUserStats,
    logout,
    loading,
    firebaseConfigured: !!supabase // True if supabase is configured
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};